import { Route, Router } from '@angular/router';
import { Type } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';

import { routes } from '@app/app-routing.module';

import { ALLOWED_GUARDS } from '@config/guards.config';

import { DoodMarketplaceModel, IPageConfig } from '@core/models/marketplace.model';
import { PageFactoryComponent } from '@core/components/page-factory/page-factory.component';
import { attachCb2PreviewNavigationGuard } from '@core/services/cb2-preview/cb2-preview-navigation.guard';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';

/** Deps required to register marketplace pages as Angular routes. */
export interface CreateMarketplaceRoutesDeps {
  router: Router;
  localizeRouterService: LocalizeRouterService;
  routerHelperService: RouterHelperService;
}

/**
 * Registers the marketplace pages as Angular routes (mutates the router config).
 *
 * Kept in its own module so the heavy `routes` / `PageFactoryComponent` graph is
 * only pulled in by callers that actually build routes, keeping the pure
 * runtime helpers (and their tests) free of the app-routing dependency tree.
 */
export function createMarketplaceRoutes(
  marketplace: DoodMarketplaceModel,
  deps: CreateMarketplaceRoutesDeps,
): void {
  if (!marketplace.pages) {
    return;
  }

  const marketplaceRoutes = marketplace.pages.map(page =>
    createRoute(page, deps.routerHelperService),
  );

  const wildcard = routes.find(route => route.path === '**');
  routes.splice(routes.indexOf(wildcard!), 1);

  const newRoutes = [...marketplaceRoutes, ...routes];
  const orderedRoutes = orderRoutes(newRoutes);
  if (orderedRoutes) {
    orderedRoutes.push(wildcard!);
    deps.localizeRouterService.parser.load(orderedRoutes).then();
  }

  deps.router.config[0].children?.push(...marketplaceRoutes);
  attachCb2PreviewNavigationGuard(deps.router.config);
}

function orderRoutes(newRoutes: Route[]): Route[] {
  function convertTo01(path?: string): string {
    if (!path) {
      return '2';
    }
    let split = path.split('/');
    split = split.map(s => s.replace(/:.*/g, '1'));
    split = split.map(s => (s === '1' ? '1' : '0'));
    return split.join('');
  }

  return [...newRoutes].sort((a, b) => {
    return convertTo01(a.path) < convertTo01(b.path) ? -1 : 1;
  });
}

function createRoute(page: IPageConfig, routerHelperService: RouterHelperService): Route {
  let children: Array<Route> = [];

  if (page.redirect_to) {
    return {
      path: page.path,
      redirectTo: page.path
        ? (routerHelperService.translateRoute(page.redirect_to) as string)
        : page.redirect_to,
      pathMatch: 'full',
    };
  }

  if (page.children) {
    const childrenRoutes = page.children.map(child => createRoute(child, routerHelperService));
    children = orderRoutes(childrenRoutes);
  }

  return {
    path: page.path,
    component: PageFactoryComponent,
    canActivate: mapGuards(page.guards),
    children,
    data: { block: page.block },
  };
}

function mapGuards(guardHandles: Array<string | Type<unknown>> | undefined): Array<Type<unknown>> {
  if (!guardHandles) {
    return [];
  }
  const guards: Array<Type<unknown>> = [];
  for (const guardHandle of guardHandles) {
    const key = guardHandle.toString();
    if (!ALLOWED_GUARDS[key]) {
      console.error('Guard not found ' + key);
      continue;
    }
    guards.push(ALLOWED_GUARDS[key]);
  }
  return guards;
}
